import { Flex, Box, VStack } from '@chakra-ui/react';
import { FC, useContext, useMemo } from 'react';
import PARAGRAPH_MAPPING from '../../config/paragraph-mapping';
import EntityContext from '../../context/EntityContext';
import {
  ActivityPoiContentParagraphs,
  CommentFragment,
  ParagraphSidebarBlockFragment,
} from '../../generated/types';
import { CommentList } from '../Common/CommentList';
import Container from './Container';

interface ArticleLayoutProps {
  paragraphs: ActivityPoiContentParagraphs[];
  sidebarParagraphs?: ParagraphSidebarBlockFragment[];
  comments?: CommentFragment[];
  totalComments?: number;
}

const paragraphHasTableOfContents = (paragraph: any) => {
  if (
    paragraph.__typename === 'ParagraphText' &&
    paragraph.addToTableOfContents
  ) {
    return true;
  }

  return false;
};

export const ArticleLayout: FC<ArticleLayoutProps> = ({
  paragraphs,
  sidebarParagraphs,
  comments,
  totalComments,
}) => {
  const entityContext = useContext(EntityContext);

  const hasSidebar = useMemo(() => {
    let hasTableOfContents = false;
    const entityType = entityContext.entity.__typename;

    if (sidebarParagraphs?.length > 0) {
      return true;
    }

    if (entityType === 'Activity') {
      hasTableOfContents = entityContext.entity.activityContent.some(
        paragraphHasTableOfContents
      );
    } else if (entityType === 'Poi') {
      // TODO: need to implement this in pois
      hasTableOfContents = entityContext.entity.poiContent.some(
        paragraphHasTableOfContents
      );
    }

    return hasTableOfContents;
  }, [entityContext.entity]);

  return (
    <Container paddingY={4}>
      <Flex
        flexDirection={{
          base: 'column',
          md: 'column',
          lg: 'row',
        }}
      >
        <Box flex={1} overflow="hidden">
          <Box
            className="ContentWrapper"
            __css={{
              '.ParagraphText': {
                my: 0,
              },
            }}
          >
            <VStack spacing={8} align="flex-start">
              {paragraphs?.map((data, i) => {
                if (PARAGRAPH_MAPPING[data.__typename]) {
                  const Component = PARAGRAPH_MAPPING[data.__typename];
                  return (
                    <Component
                      key={`${data.id}_${i}`}
                      data={data}
                      isInArticleLayout
                    />
                  );
                }

                return null;
              })}
            </VStack>

            {comments && (
              <Box mt={12}>
                <CommentList
                  comments={comments}
                  totalComments={totalComments}
                />
              </Box>
            )}
          </Box>
        </Box>
        {hasSidebar && (
          <Box
            flex="0 0 auto"
            width={{
              lg: '500px',
            }}
            mt={{
              base: 12,
              md: 0,
            }}
            ml={{
              base: 0,
              md: 12,
            }}
          >
            {sidebarParagraphs?.map((data, i) => {
              if (PARAGRAPH_MAPPING[data.__typename]) {
                const Component = PARAGRAPH_MAPPING[data.__typename];
                return (
                  <Component
                    key={`${data.id}_${i}`}
                    data={data}
                    isInArticleLayout
                  />
                );
              }

              return null;
            })}
          </Box>
        )}
      </Flex>
    </Container>
  );
};
